// React
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

// Form seam (GRAIN-2551 shared TanStack + Zod primitives)
import { Form } from '@components/Form';
import { useFormContext, type FormApi } from '@components/Form/FormProvider';
import { shouldShowFieldError, useFieldErrorVisibility } from '@components/Form/fieldErrorVisibility';

// UI primitives
import { Field, FieldLabel, FieldContent, FieldError } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// Icons
import { infoIcon } from './InfoIcon';

// Local components
import { SubmitButton } from './SubmitButton';

// Hooks and utilities
import { useCreateContact } from '@hooks/contacts';
import { useServerErrors, type ServerErrorMap } from '@hooks/useServerErrors';
import { useTranslation } from '@hooks/useTranslation';
import { toast } from '@/hooks/use-toast';
import { getFieldErrors } from '@api/client';
import { CONTACT_EMAIL_REGEX, computePhoneFieldMessage, isValidContactMobile } from '@/react/schemas/contact.schema';
import { optionalPattern } from '@/react/schemas/zodPatterns';
import type { ApiContact } from '@api/contacts';

import { FIELD_SURFACE, FIELD_SURFACE_BORDERED } from "@/components/ui/field-surface";
interface CreateCustomerFormProps {
  onSuccess: (contact: ApiContact) => void;
  onPendingChange?: (isPending: boolean) => void;
}

const labelClass = 'font-inter text-sm font-semibold text-grey leading-normal';
const optionalClass = 'font-inter text-sm font-normal text-grey leading-normal';
const inputClass =
  cn(FIELD_SURFACE_BORDERED, 'h-[45px] w-full px-6 py-4 text-sm text-off-black placeholder:text-grey-light focus-visible:outline-none focus-visible:ring-0');
const invalidInputClass = 'border-chilli-red-light bg-chilli-blush';

type CustomerServerErrorTarget = 'email' | 'full_name' | 'phone_number';

const CUSTOMER_SERVER_ERROR_TARGETS: ServerErrorMap<CustomerServerErrorTarget> = {
  email: ['email'],
  contact: ['email'],
  first_name: ['full_name'],
  last_name: ['full_name'],
  full_name: ['full_name'],
  mobile_number: ['phone_number'],
  phone_number: ['phone_number'],
};

const CUSTOMER_BASE_ERROR_TARGETS = [] as const;

function makeCreateCustomerSchema(t: (key: string) => string) {
  return z.object({
    email: z
      .string()
      .min(1, t('create_customer.validation.email_required'))
      .regex(optionalPattern(CONTACT_EMAIL_REGEX), t('create_customer.validation.email_invalid')),
    full_name: z.string().min(1, t('create_customer.validation.full_name_required')),
    phone_number: z.string().min(1, t('create_customer.validation.phone_required')),
    organisation_name: z.string().optional().default(''),
    billing_address: z.string().optional().default(''),
    postal_code: z
      .string()
      .regex(/^(?:\d{4,6})?$/, t('create_customer.validation.postal_code_invalid'))
      .optional()
      .default(''),
    customer_note: z.string().optional().default(''),
  });
}

type CreateCustomerFormData = z.infer<ReturnType<typeof makeCreateCustomerSchema>>;

export function CreateCustomerForm({ onSuccess, onPendingChange }: CreateCustomerFormProps) {
  const { t } = useTranslation();
  const createContact = useCreateContact();
  const schema = useMemo(() => makeCreateCustomerSchema(t), [t]);

  const formRef = useRef<FormApi | null>(null);
  const [submitError, setSubmitError] = useState<Error | null>(null);
  useServerErrors<CustomerServerErrorTarget>({
    error: submitError,
    formRef,
    mapServerFieldToTargets: CUSTOMER_SERVER_ERROR_TARGETS,
    baseErrorTargets: CUSTOMER_BASE_ERROR_TARGETS,
  });

  // Lets a hosting dialog gate its user-initiated close paths while the create
  // request is in flight. The cleanup clears the flag on unmount so a dialog can
  // never stay latched shut; it also runs between dep changes, but React commits
  // that false with the following true, so the parent never sees the gap.
  useEffect(() => {
    onPendingChange?.(createContact.isPending);
    return () => onPendingChange?.(false);
  }, [createContact.isPending, onPendingChange]);

  const handleSubmit = useCallback(
    async (values: CreateCustomerFormData) => {
      // libphone format can't live in the schema (adapter dead-locks on refine);
      // the inline format error is already visible, so bail before mutating.
      if (!isValidContactMobile(values.phone_number)) return;
      setSubmitError(null);

      const nameParts = values.full_name.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      try {
        const contact = await createContact.mutateAsync({
          first_name: firstName,
          last_name: lastName,
          email: values.email.trim(),
          mobile_number: values.phone_number.trim(),
          accepts_marketing: false,
          notes: values.customer_note.trim() || null,
          organisation_name: values.organisation_name.trim() || undefined,
          address: values.billing_address.trim() || undefined,
          postal_code: values.postal_code.trim() || undefined,
        });
        onSuccess(contact);
      } catch (err: unknown) {
        setSubmitError(err as Error);
        const fieldErrors = getFieldErrors(err);
        const mappedToField = fieldErrors
          ? Object.keys(fieldErrors).some((key) => CUSTOMER_SERVER_ERROR_TARGETS[key])
          : false;
        if (!mappedToField) {
          toast({
            description: err instanceof Error ? err.message : t('create_customer.errors.create_failed'),
            variant: 'destructive',
          });
        }
      }
    },
    [createContact, onSuccess, t],
  );

  return (
    <Form<CreateCustomerFormData>
      schema={schema}
      defaultValues={{
        email: '',
        full_name: '',
        phone_number: '',
        organisation_name: '',
        billing_address: '',
        postal_code: '',
        customer_note: '',
      }}
      formRef={formRef}
      onSubmit={handleSubmit}
      isPending={createContact.isPending}
      className="flex flex-col gap-8 overflow-x-hidden px-8 pt-6 font-inter"
    >
      <CreateCustomerFields />
    </Form>
  );
}

function CreateCustomerFields() {
  const { form } = useFormContext();
  const { t } = useTranslation();
  const submitted = useFieldErrorVisibility();

  return (
    <>
      <div className="grid grid-cols-2 gap-8">
        <form.Field name="email">
          {(field) => {
            const isInvalid = shouldShowFieldError(field.state.meta, submitted);
            return (
              <Field>
                <FieldLabel className={labelClass}>{t('create_customer.labels.email')}</FieldLabel>
                <FieldContent>
                  <Input
                    name="email"
                    type="email"
                    autoComplete="email"
                    aria-invalid={isInvalid || undefined}
                    value={(field.state.value as string) || ''}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    placeholder={t('create_customer.placeholders.email')}
                    className={cn(inputClass, isInvalid && invalidInputClass)}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </FieldContent>
              </Field>
            );
          }}
        </form.Field>

        <form.Field name="full_name">
          {(field) => {
            const isInvalid = shouldShowFieldError(field.state.meta, submitted);
            return (
              <Field>
                <FieldLabel className={labelClass}>{t('create_customer.labels.full_name')}</FieldLabel>
                <FieldContent>
                  <Input
                    name="full_name"
                    autoComplete="name"
                    aria-invalid={isInvalid || undefined}
                    value={(field.state.value as string) || ''}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    placeholder={t('create_customer.placeholders.full_name')}
                    className={cn(inputClass, isInvalid && invalidInputClass)}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </FieldContent>
              </Field>
            );
          }}
        </form.Field>
      </div>

      <div className="grid grid-cols-2 gap-8">
        <form.Field name="phone_number">
          {(field) => {
            const value = (field.state.value as string) || '';
            const message = computePhoneFieldMessage(
              field.state.meta,
              value,
              submitted,
              t('create_customer.validation.phone_invalid'),
            );
            return (
              <Field>
                <FieldLabel className={labelClass}>{t('create_customer.labels.phone_number')}</FieldLabel>
                <FieldContent>
                  <Input
                    name="phone_number"
                    type="tel"
                    autoComplete="tel"
                    aria-invalid={Boolean(message) || undefined}
                    value={value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    placeholder={t('create_customer.placeholders.phone')}
                    className={cn(inputClass, message && invalidInputClass)}
                  />
                  {message && <FieldError>{message}</FieldError>}
                </FieldContent>
              </Field>
            );
          }}
        </form.Field>

        <form.Field name="organisation_name">
          {(field) => (
            <Field>
              <div className="flex items-center justify-between">
                <FieldLabel className={labelClass}>{t('create_customer.labels.organisation')}</FieldLabel>
                <span className={optionalClass}>{t('create_customer.labels.optional')}</span>
              </div>
              <FieldContent>
                <Input
                  name="organisation_name"
                  autoComplete="organization"
                  value={(field.state.value as string) || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t('create_customer.placeholders.organisation')}
                  className={inputClass}
                />
              </FieldContent>
            </Field>
          )}
        </form.Field>
      </div>

      <div className="grid grid-cols-2 gap-8">
        <form.Field name="billing_address">
          {(field) => (
            <Field>
              <div className="flex items-center justify-between">
                <FieldLabel className={labelClass}>{t('create_customer.labels.billing_address')}</FieldLabel>
                <span className={optionalClass}>{t('create_customer.labels.optional')}</span>
              </div>
              <FieldContent>
                <Input
                  name="billing_address"
                  autoComplete="street-address"
                  value={(field.state.value as string) || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t('create_customer.placeholders.billing_address')}
                  className={inputClass}
                />
              </FieldContent>
            </Field>
          )}
        </form.Field>

        <form.Field name="postal_code">
          {(field) => {
            const isInvalid = shouldShowFieldError(field.state.meta, submitted);
            return (
              <Field>
                <div className="flex items-center justify-between">
                  <FieldLabel className={labelClass}>{t('create_customer.labels.postal_code')}</FieldLabel>
                  <span className={optionalClass}>{t('create_customer.labels.optional')}</span>
                </div>
                <FieldContent>
                  <Input
                    name="postal_code"
                    autoComplete="postal-code"
                    inputMode="numeric"
                    aria-invalid={isInvalid || undefined}
                    value={(field.state.value as string) || ''}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    placeholder={t('create_customer.placeholders.postal_code')}
                    className={cn(inputClass, isInvalid && invalidInputClass)}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </FieldContent>
              </Field>
            );
          }}
        </form.Field>
      </div>

      <form.Field name="customer_note">
        {(field) => (
          <Field>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FieldLabel className={labelClass}>{t('create_customer.labels.customer_note')}</FieldLabel>
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" aria-label="More info" className="text-grey">
                        {infoIcon}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{t('create_order.labels.tooltip_internal')}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <span className={optionalClass}>{t('create_customer.labels.optional')}</span>
            </div>
            <FieldContent>
              <Textarea
                value={(field.state.value as string) || ''}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                placeholder={t('create_customer.placeholders.customer_note')}
                className={cn(FIELD_SURFACE, "h-[84px] resize-none px-6 py-4 text-sm text-off-black placeholder:text-grey-light focus-visible:ring-0")}
              />
            </FieldContent>
          </Field>
        )}
      </form.Field>

      <div className="-mx-8 border-t border-dinnerplate-white px-8 py-6">
        <div className="flex justify-end">
          <SubmitButton label={t('create_customer.actions.create')} />
        </div>
      </div>
    </>
  );
}
